// AIE-531 routing fix (Phase 5): assemble the OptimizationRequest, merging the chosen feature
// config into the SAME `parameters` channel the worker consumes. Extracted from
// NewStudyModern.handleSubmit so the routing seam is unit-testable. The dead `parameter_info`
// channel is NEVER populated — populating it alone would make the worker ignore request.parameters
// (the `or` in `request.parameter_info or request.parameters`) and drop every strategy param.
import type {
  DataPoint,
  FeatureTypeSpec,
  OptimizationParameters,
  OptimizationRequest,
  SlotDefault,
} from "@/types/api";
import type { Feature } from "@/components/feature-builder/FeatureBuilder";
import { buildFeatureParams, deriveSlotStates } from "@/utils/featureParams";
import type { FeatureParamState } from "@/utils/featureParams";

export interface OptimizationRequestArgs {
  studyName: string;
  nTrials: number;
  symbol: string;
  timeframe: string;
  data: DataPoint[];
  /** Processed strategy params (range/pinned keys + minimum_trades / objective_function). */
  strategyParameters: Record<string, unknown>;
  features: Feature[];
  /** Per-param pin/range UI state (Phase 6). Empty -> every feature param pinned at its scalar. */
  featureParamState: FeatureParamState;
  /** Flag gate (optimizer.feature_param_optimization). When false, featureParamState is IGNORED so
   *  ranges set while the flag was on are not emitted — the flag gates range OPTIMIZATION, not just
   *  UI visibility. The fixed feature SELECTION (type/period) still routes unconditionally. */
  rangeEnabled: boolean;
  slotDefaults: SlotDefault[];
  featureTypes: Record<string, FeatureTypeSpec>;
  tomlConfig: string;
  minimumTrades: number;
  objectiveFunction: "sortino" | "sharpe";
  samplerSeed: number | null;
  parallelCount: number;
}

export function buildOptimizationRequest(
  args: OptimizationRequestArgs,
): OptimizationRequest {
  // deriveSlotStates maps the per-param pin/range UI state into SlotState[] (pinned -> scalar,
  // unpinned -> int range spec). Empty featureParamState -> all pinned at the feature's scalars.
  const featureSlotStates = deriveSlotStates(
    args.features,
    args.rangeEnabled ? args.featureParamState : {},
    args.featureTypes,
  );
  // Conditional emission: only deviating/ranged slots add fN_* keys, so an all-default study keeps
  // a byte-identical `parameters` payload.
  const parameters = {
    ...args.strategyParameters,
    ...buildFeatureParams(
      featureSlotStates,
      args.slotDefaults,
      args.featureTypes,
    ),
  } as unknown as OptimizationParameters;

  return {
    ...(args.studyName ? { study_name: args.studyName } : {}),
    n_trials: Number(args.nTrials),
    symbol: args.symbol,
    timeframe: args.timeframe,
    data: args.data,
    parameters,
    toml_config: args.tomlConfig,
    min_trades_required: args.minimumTrades,
    objective_function: args.objectiveFunction,
    ...(args.samplerSeed !== null ? { sampler_seed: args.samplerSeed } : {}),
    ...(args.parallelCount > 1 ? { parallel_count: args.parallelCount } : {}),
  };
}
